"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import CTALink from "@/components/CTA/ctaLink";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { cn } from "@/lib/utils";
import { useInView } from "react-intersection-observer";

export type ProductCardListExpandableFooterProps = {
  headingText?: string;
  isExpanded: boolean;
  onToggle: (label: string) => void;
  toggleLabel: string;
  children: React.ReactNode;
  searchCtaUrl?: string | null;
  searchCtaLabel: string;
  location?: string;
  onViewed?: (label: string) => void;
  className?: string;
};

function ProductCardListExpandableFooter({
  headingText = "Not finding what you want?",
  isExpanded,
  onToggle,
  toggleLabel,
  children,
  searchCtaUrl,
  searchCtaLabel,
  location,
  onViewed,
  className,
}: ProductCardListExpandableFooterProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState("0px");
  const { ref: viewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);
  const reactDomId = useId();
  const expandablePanelId = `product-card-list-expandable-panel-${reactDomId}`;
  const toggleButtonId = `product-card-list-expandable-toggle-${reactDomId}`;

  useEffect(() => {
    if (inView) {
      onViewed?.(toggleLabel);
    }
  }, [inView, onViewed, toggleLabel]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }

    const updateHeight = () => {
      if (!contentRef.current) {
        return;
      }
      setHeight(isExpanded ? `${contentRef.current.scrollHeight}px` : "0px");
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      if (isExpanded) {
        updateHeight();
      }
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isExpanded]);

  return (
    <div ref={viewRef} className={cn("w-full", className)}>
      <div
        id={expandablePanelId}
        ref={contentRef}
        style={{
          height,
          overflow: "hidden",
          transition: "height 0.5s ease",
        }}
        aria-hidden={!isExpanded}
      >
        {children}
      </div>

      <div
        className={cn(
          "w-full bg-putty-100 px-4 py-6 sm:px-6",
          "flex flex-col items-center gap-4 text-center",
        )}
        {...(isExpanded
          ? { "aria-labelledby": `${toggleButtonId}-heading` }
          : {})}
      >
        {isExpanded && (
          <h3
            id={`${toggleButtonId}-heading`}
            className="text-md md:text-base font-bold leading-tight text-gray-dark m-0"
          >
            {headingText}
          </h3>
        )}

        <div
          className={cn(
            "flex flex-row w-full max-w-3xl flex-wrap items-center justify-center gap-6",
            isExpanded && "md:flex-row-reverse",
          )}
        >
          {isExpanded && searchCtaUrl && (
            <CTALink
              label={searchCtaLabel}
              url={searchCtaUrl}
              type="brand"
              className="rounded-full px-6 py-[0.625rem] h-[2.625rem] text-sm md:text-md"
              location={location}
              html_id={`product-card-list-expanded-search-cta-${reactDomId}`}
              elementType={ElementType.Link}
            />
          )}

          <button
            id={toggleButtonId}
            type="button"
            className={cn(
              "cursor-pointer p-0 text-lg font-normal",
              "text-neutral-700 underline underline-offset-4 decoration-neutral-700",
              "transition-colors hover:text-neutral-700 hover:no-underline",
            )}
            aria-expanded={isExpanded}
            aria-controls={expandablePanelId}
            onClick={() => onToggle(toggleLabel)}
          >
            {toggleLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductCardListExpandableFooter;
